/**
 * cache/reverse-index.ts — entityId → queryKey[] reverse index (T7431).
 *
 * The SDK maintains a reverse index so a realtime event for a specific
 * entity can find every cached query referencing it in O(1) without
 * scanning the QueryCache. Two layers compose:
 *
 *   1. This module — the typed `ReverseIndex` interface + a default
 *      `Map<entityId, Set<QueryKey>>` implementation.
 *   2. `reverse-index-eviction.ts` — `QueryCache.subscribe('removed')`
 *      walks the removed query's ids and prunes them via
 *      `ReverseIndex.removeKeyForId`.
 *
 * For the in-flight add/remove diff on `'updated'` actions, see
 * `reverse-index-diff.ts` — that module owns the side-map state and the
 * `applyAdded` / `applyUpdated` / `applyRemoved` helpers that mutate
 * this index.
 *
 * Failure mode this module prevents: a realtime event walks the index
 * and follows stale pointers to queries that have been evicted by
 * TanStack's GC — the dispatcher tries to invalidate a missing query
 * and emits a noisy noop telemetry per event.
 */

import type { QueryKey } from '@tanstack/react-query';

/**
 * Reverse-index public surface. Hot-path callers should use `lookup` —
 * O(1) Map.get + Set iteration. Mutators are called by the eviction
 * subscriber and the cache-event diff helpers.
 */
export interface ReverseIndex {
  /** Add `queryKey` to the set associated with `entityId`. */
  addKeyForId(entityId: string, queryKey: QueryKey): void;
  /** Remove `queryKey` from `entityId`'s set; drops empty sets. */
  removeKeyForId(entityId: string, queryKey: QueryKey): void;
  /** Iterate every queryKey that references `entityId`. */
  lookup(entityId: string): IterableIterator<QueryKey>;
  /** Diagnostic — total number of distinct entity ids tracked. */
  size(): number;
  /** Test-only: empty the index. */
  clear(): void;
}

/**
 * Default `Map<string, Set<QueryKey>>` implementation. Single instance
 * per QueryClient — keyed via `WeakMap<QueryClient, ReverseIndex>` at
 * the SDK provider site so network-switching discards both the
 * QueryClient and its reverse index together (no cross-network leak —
 * cache-miss vector 9, file 05 §1935).
 */
export function createReverseIndex(): ReverseIndex {
  const index = new Map<string, Set<QueryKey>>();

  return {
    addKeyForId(entityId, queryKey) {
      let set = index.get(entityId);
      if (set === undefined) {
        set = new Set<QueryKey>();
        index.set(entityId, set);
      }
      set.add(queryKey);
    },
    removeKeyForId(entityId, queryKey) {
      const set = index.get(entityId);
      if (set === undefined) return;
      set.delete(queryKey);
      if (set.size === 0) index.delete(entityId);
    },
    *lookup(entityId) {
      const set = index.get(entityId);
      if (set === undefined) return;
      for (const k of set) yield k;
    },
    size() {
      return index.size;
    },
    clear() {
      index.clear();
    },
  };
}
